import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listUserPlugins,
  getUserPluginFilterOptions,
} from '@kirin/api-client/api/plugins';
import type {
  PluginListItem,
  PluginSortField,
  PluginFilterOptionField,
} from '@kirin/api-client/contracts/plugins';
import { apiClient } from '@/lib/api-client';

/**
 * Fetch the user's own plugins on the canonical DataTable contract. Filters are
 * the engine's canonical `FilterValue[]` passed straight through (mirrors
 * `fetchOrgPlugins`) — no legacy adapter.
 */
export async function fetchPersonalPlugins(
  params: DataFetchParams
): Promise<DataFetchResponse<PluginListItem>> {
  const sortField = params.sort?.field as PluginSortField | undefined;
  const response = await listUserPlugins(apiClient, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    search: params.search || undefined,
    sortBy: sortField,
    sortOrder: params.sort?.order,
    filters: params.filters,
    knownTotal: params.knownTotal,
  });

  return {
    data: response.plugins,
    pagination: response.pagination,
    meta: { activeFilters: [] },
  };
}

/**
 * Fetch dynamic filter options for one personal Plugins column (Name /
 * Marketplace). Mirrors `fetchPluginFieldFilterOptions` without the org id.
 */
export async function fetchPersonalPluginFieldFilterOptions(
  field: PluginFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  // `users` is an org-only field — the personal columns never request it.
  if (field === 'users') {
    throw new Error(
      'users filter options are not available on the account scope'
    );
  }
  const response = await getUserPluginFilterOptions(apiClient, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
